import React, { useState, useEffect, useRef } from "react";
import { getAgentResult, getAgentPageResult } from "../../api/const";
import { X, Bot, Send, FileText } from "lucide-react";
import chatbotIcon from "../../assets/images/mobile_chatbot.png";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate, useParams } from "react-router-dom";

const MobileAgentChatbot = ({ onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [activeTool, setActiveTool] = useState(null);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const navigate = useNavigate();
  const params = useParams();

  const [pageResult, setPageResult] = useState("");
  const [pageLoading, setPageLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  // 🔥 모바일 메뉴 열림 감지
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Bottom Sheet 드래그 상태
  const [sheetHeight, setSheetHeight] = useState(70);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startHeight, setStartHeight] = useState(70);
  const sheetRef = useRef(null);

  const SNAP_POINTS = {
    MIN: 30,
    MID: 70,
    MAX: 95
  };

  const toolButtons = [
    { id: "chatbot", label: "챗봇과 대화", icon: <Bot size={20} className="text-[#1A1B2E]" /> },
    { id: "page", label: "현재 페이지 분석", icon: <FileText size={20} className="text-[#1A1B2E]" /> },
  ];

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, loading]);

  // 🔥 body의 mobile-menu-open 클래스 감지
  useEffect(() => {
    const checkMenuState = () => {
      setIsMobileMenuOpen(document.body.classList.contains('mobile-menu-open'));
    };

    // 초기 체크
    checkMenuState();

    // MutationObserver로 body 클래스 변화 감지
    const observer = new MutationObserver(checkMenuState);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      if (onOpenChange) onOpenChange(true);
    } else {
      document.body.style.overflow = "unset";
      if (onOpenChange) onOpenChange(false);
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onOpenChange]);

  // Bottom Sheet 드래그 핸들러
  const handleDragStart = (e) => {
    setIsDragging(true);
    setStartY(e.touches ? e.touches[0].clientY : e.clientY);
    setStartHeight(sheetHeight);
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;

    const currentY = e.touches ? e.touches[0].clientY : e.clientY;
    const deltaY = startY - currentY;
    const windowHeight = window.innerHeight;
    const deltaPercent = (deltaY / windowHeight) * 100;

    let newHeight = startHeight + deltaPercent;
    newHeight = Math.max(SNAP_POINTS.MIN, Math.min(SNAP_POINTS.MAX, newHeight));

    setSheetHeight(newHeight);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);

    const snapPoints = Object.values(SNAP_POINTS);
    const closest = snapPoints.reduce((prev, curr) =>
      Math.abs(curr - sheetHeight) < Math.abs(prev - sheetHeight) ? curr : prev
    );

    setSheetHeight(closest);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowMenu(false);
    setActiveTool("chatbot");
    setSheetHeight(SNAP_POINTS.MID);
  };

  const handleClose = () => {
    setIsOpen(false);
    setActiveTool(null);
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const handleToolSelect = (toolId) => {
    setActiveTool(toolId);
    setIsOpen(true);
    setShowMenu(false);
    setSheetHeight(SNAP_POINTS.MID);
  };

  const handleAsk = async () => {
    if (!question.trim()) return;
    const userMsg = { role: "user", content: question };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setQuestion("");
    setLoading(true);

    try {
      const result = await getAgentResult({ question });
      const aiMsg = {
        role: "ai",
        content: result.answer,
        routing: result.routing,
        references: result.references
      };
      setMessages([...newMessages, aiMsg]);
    } catch (e) {
      console.error(e);
      setMessages([...newMessages, { role: "ai", content: "오류가 발생했습니다." }]);
    } finally {
      setLoading(false);
    }
  };

  const handlePageAnalyze = async () => {
    setPageLoading(true);
    setPageError("");
    setPageResult("");
    try {
      const currentPath = window.location.pathname;
      const result = await getAgentPageResult({ path: currentPath });
      setPageResult(result.analysis || "분석 결과가 없습니다.");
    } catch (e) {
      console.error(e);
      setPageError("페이지 분석 중 오류가 발생했습니다.");
    } finally {
      setPageLoading(false);
    }
  };

  // 닫힌 상태: Speed Dial
  // 🔥 모바일 메뉴가 열려있으면 챗봇 숨김
  if (!isOpen) {
    if (isMobileMenuOpen) {
      return null;
    }

    return (
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end space-y-3 safe-area-bottom mobile-menu-open:hidden">
        {showMenu && (
          <div className="flex flex-col space-y-3 mb-2 animate-in slide-in-from-bottom-5 fade-in duration-300">
            {toolButtons.map((btn, idx) => (
              <button
                key={btn.id}
                onClick={() => handleToolSelect(btn.id)}
                className="flex items-center justify-end group animate-in slide-in-from-right-5 fade-in duration-200"
                style={{ animationDelay: `${idx * 50}ms` }}
              >
                <div className="mr-3 px-4 py-2 bg-white rounded-xl shadow-lg text-sm font-semibold text-gray-800 transition-all duration-200 border border-gray-100 whitespace-nowrap">
                  {btn.label}
                </div>
                <div className="w-14 h-14 bg-white rounded-full shadow-xl flex items-center justify-center border-2 border-gray-100 active:scale-95 transition-transform duration-200 hover:shadow-2xl min-h-[56px] min-w-[56px]">
                  {btn.icon}
                </div>
              </button>
            ))}
          </div>
        )}

        <button
          onClick={toggleMenu}
          className="bg-gradient-to-br from-[#1A1B2E] via-[#262742] to-[#2D2F4A] text-white px-6 py-3.5 rounded-full shadow-2xl active:shadow-xl active:scale-95 transition-all duration-300 border border-white/20 min-h-[56px] min-w-[56px] flex items-center justify-center"
          style={{ boxShadow: '0 8px 32px rgba(26, 27, 46, 0.5)' }}
        >
          {showMenu ? (
            <X size={24} className="animate-in spin-in-90 duration-200" />
          ) : (
            <span className="text-sm font-bold tracking-wide animate-in fade-in duration-200">P6ix AI</span>
          )}
        </button>
      </div>
    );
  }

  // 열린 상태: Draggable Bottom Sheet
  return (
    <>
      {/* 배경 오버레이 */}
      <div
        className="fixed inset-0 bg-black/40 z-40 animate-in fade-in duration-300"
        onClick={handleClose}
      />

      {/* Bottom Sheet */}
      <div
        ref={sheetRef}
        className="fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl flex flex-col safe-area-bottom"
        style={{
          height: `${sheetHeight}vh`,
          transition: isDragging ? 'none' : 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          maxHeight: '95vh'
        }}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
        onMouseMove={handleDragMove}
        onMouseUp={handleDragEnd}
      >
        {/* 드래그 핸들 영역 */}
        <div
          className="flex-shrink-0 pt-3 pb-2 px-4 cursor-grab active:cursor-grabbing select-none"
          onTouchStart={handleDragStart}
          onMouseDown={handleDragStart}
        >
          {/* 드래그 핸들 바 */}
          <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-3 transition-all duration-200" />

          {/* 헤더 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl flex items-center justify-center shadow-sm">
                {activeTool === "chatbot" ? (
                  <img src={chatbotIcon} alt="AI" className="w-5 h-5" />
                ) : (
                  <FileText size={18} className="text-[#1A1B2E]" />
                )}
              </div>
              <span className="font-bold text-lg text-[#1A1B2E]">
                {activeTool === "chatbot" ? "P6ix AI" : "페이지 분석"}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="p-2 -mr-2 text-gray-500 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* 컨텐츠 영역 */}
        <div className="flex-1 overflow-hidden flex flex-col bg-gray-50">
          {activeTool === "page" ? (
            /* 페이지 분석 UI */
            <>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="bg-white rounded-xl shadow-sm border p-4 mb-4">
                  <h3 className="font-bold text-gray-800 mb-2">현재 페이지 인사이트</h3>
                  <p className="text-gray-500 text-sm">
                    현재 보고 계신 화면의 데이터와 현황을 AI가 분석합니다.
                    <br />
                    (예: 공정 지연 원인, 안전 위험 요소 등)
                  </p>
                </div>

                {pageLoading && (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1A1B2E]"></div>
                  </div>
                )}

                {pageError && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm border border-red-100">
                    {pageError}
                  </div>
                )}

                {pageResult && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-lg shadow-sm max-w-[90%] bg-white border text-gray-800">
                      <div className="flex items-center mb-1 text-[#1A1B2E]">
                        <Bot size={14} className="mr-1" />
                        <span className="text-xs font-semibold">AI</span>
                      </div>
                      <div className="text-sm leading-relaxed prose prose-sm max-w-none">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {String(pageResult)}
                        </ReactMarkdown>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* 분석 버튼 */}
              <div className="flex-shrink-0 bg-white border-t p-2 safe-area-bottom">
                <button
                  onClick={handlePageAnalyze}
                  disabled={pageLoading}
                  className="w-full text-white py-3 rounded-full font-medium disabled:opacity-50 transition-all active:scale-95"
                  style={{ backgroundColor: "#1A1B2E" }}
                >
                  {pageLoading ? "분석 중..." : "현재 페이지 분석하기"}
                </button>
              </div>
            </>
          ) : (
            /* 챗봇 UI */
            <>
              <div className="flex-1 overflow-y-auto bg-gray-50 p-4 space-y-3">
                {messages.length > 0 ? (
                  messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl shadow-sm text-sm leading-relaxed ${msg.role === "user"
                          ? "bg-[#1A1B2E] text-white rounded-br-none"
                          : "bg-white text-gray-800 border border-gray-100 rounded-bl-none"
                          }`}
                      >
                        {msg.role === "ai" && (
                          <div className="flex items-center mb-1 text-[#1A1B2E]">
                            <Bot size={14} className="mr-1" />
                            <span className="text-xs font-semibold">AI</span>
                          </div>
                        )}

                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            a: ({ node, ...props }) => <span className="text-blue-500 font-medium" {...props} />
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>

                        {msg.routing && msg.routing.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {msg.routing.map((route, rIdx) => (
                              <button
                                key={rIdx}
                                onClick={() => {
                                  handleClose();
                                  let finalPath = route.path;
                                  const projectId = params.id || params.projectId;
                                  if (projectId) {
                                    finalPath = finalPath.replace(":id", projectId).replace(":projectId", projectId);
                                  }
                                  navigate(finalPath);
                                }}
                                className="text-xs px-3 py-1.5 bg-blue-50 text-blue-600 rounded-full border border-blue-100 active:bg-blue-100 transition-colors flex items-center"
                              >
                                <span className="mr-1">🔗</span>
                                {route.title}
                              </button>
                            ))}
                          </div>
                        )}

                        {msg.references && msg.references.length > 0 && (
                          <div className="mt-3 pt-2 border-t border-gray-100">
                            <p className="text-[11px] text-gray-500 font-semibold mb-1">참조 법령(상세보기)</p>
                            <div className="space-y-1">
                              {msg.references.map((ref, rIdx) => (
                                <details key={rIdx} className="group bg-gray-50 rounded border border-gray-200 open:bg-white open:shadow-sm transition-all">
                                  <summary className="cursor-pointer p-2 text-[11px] font-bold text-blue-800 flex items-center justify-between select-none">
                                    <span>[{ref.law_name} {ref.hierarchy}]</span>
                                    <span className="text-gray-400 group-open:rotate-180 transition-transform">▼</span>
                                  </summary>
                                  <div className="px-2 pb-2 text-[11px] text-gray-600 leading-relaxed whitespace-pre-wrap border-t border-gray-100 mt-1 pt-1">
                                    {ref.text}
                                  </div>
                                </details>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center px-6">
                    <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <Bot className="w-10 h-10 text-blue-500" />
                    </div>
                    <p className="text-gray-900 font-semibold text-base mb-2">P6ix AI에게 물어보세요</p>
                    <p className="text-gray-500 text-sm leading-relaxed">
                      프로젝트 관련 질문을 입력하면<br />
                      AI가 답변해드립니다
                    </p>
                  </div>
                )}
                {loading && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 bg-white rounded-lg shadow-sm border">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                        <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* 입력창 */}
              <div className="flex-shrink-0 bg-white border-t border-gray-200 px-3 py-3 safe-area-bottom shadow-lg">
                <div className="flex items-center bg-gray-50 rounded-2xl px-4 py-2.5 border border-gray-200">
                  <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleAsk()}
                    placeholder="무엇이든 물어보세요..."
                    className="flex-1 bg-transparent outline-none text-[15px] text-gray-800 placeholder-gray-400"
                  />
                  <button
                    onClick={handleAsk}
                    disabled={!question.trim() || loading}
                    className="ml-2 p-2.5 bg-gradient-to-br from-[#1A1B2E] to-[#2D2F4A] text-white rounded-full disabled:opacity-40 disabled:bg-gray-300 active:scale-95 transition-all duration-200 shadow-md min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <Send size={18} />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MobileAgentChatbot;
